import { FC, useEffect, useMemo, useState, useRef, type DragEvent } from 'react'
import { KTSVG } from '../../../../_metronic/helpers'
import { getCompanyList, getVesselTypeList } from '../core/_requests'
import AddReportModal from './AddReportModal'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { Reports, Fields, AssignedReports, CompanyAdmin } from '../core/_models'
import AddFieldModal from './AddFieldModal'
import AddTabModal from './AddTabModal'
import EditFieldModal from './EditFieldModal'
import DeleteFieldModal from './DeleteFieldModal'
import EditReportModal from './EditReportModal'
import EditTabModal from './EditTabModal'
import AssignReportModal from './AssignReportModal'
import { getCompanyAdminList } from '../../Management/core/_requests'
import ConsumptionAndROB from './ConsumptionAndROB'
import { useAuth } from '../../auth'
import { useOperationsAccess } from '../core/useOperationsAccess'
import { useOperationsScopeUx } from '../core/operationsScopeUx'
import {
    canSelectOperationsLibraryModeForUser,
    coerceOperationsLibraryMode,
    resolveAuthenticatedCompanyGroupAdminId,
    resolveOperationsLibraryMode,
    resolveOperationsLibraryRoleName,
    shouldShowOperationsLibraryModeToggle,
} from '../core/libraryScopeRules'
import {
    buildLibraryMutationContext,
    buildLibraryTemplateFilters,
    hasLibraryMutationTarget,
} from '../core/libraryMutationContext'
import type { LibraryApi, LibraryMutationContext } from '../core/libraryApi'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import { TableSkeleton, SidebarTreeSkeleton } from '../../../shared/components/LoadingSkeletons'

type LibraryPageProps = {
    title?: string
    api: LibraryApi
}

const LibraryPageBase: FC<LibraryPageProps> = ({ title = 'Library', api }) => {
    // const roleId = sessionStorage.getItem("roleId");
    // const roleEntityId = sessionStorage.getItem("roleEntityId");
    const libraryApi = api
    const { currentUser, can, roleDetails } = useAuth()
    const { isSuperAdmin } = useOperationsAccess()
    const scopeUx = useOperationsScopeUx(isSuperAdmin)
    const roleEntityId = currentUser?.roleEntityId
    const normalizedRoleName = resolveOperationsLibraryRoleName(
        currentUser?.role?.roleName,
        roleDetails
    )
    const canSelectLibraryMode = canSelectOperationsLibraryModeForUser(normalizedRoleName, currentUser)
    const actsAsSuperadmin = scopeUx.actsAsSuperadmin
    const actsAsCga = scopeUx.actsAsCompanyGroupAdmin
    const authenticatedCompanyGroupAdminId = resolveAuthenticatedCompanyGroupAdminId(currentUser)
    const effectiveCgaId = actsAsCga
        ? Number(
            authenticatedCompanyGroupAdminId ??
            scopeUx.companyGroupAdminId ??
            0
        ) || undefined
        : undefined
    const companyGroupAdminId = scopeUx.companyGroupAdminId
    const [mode, setMode] = useState<'master' | 'company'>(
        resolveOperationsLibraryMode(canSelectLibraryMode, libraryApi.defaultMode)
    );
    const [companies, setCompanies] = useState<{ id: number, name: string }[]>([]);
    const [vessels, setVessels] = useState<{ id: number, name: string, imoNumber: string }[]>([]);
    const [companyAdmins, setCompanyAdmins] = useState<CompanyAdmin[]>([]);
    const [selectedVessel, setSelectedVessel] = useState<{ id: number, name: string, imoNumber: string }>({ id: 0, name: "", imoNumber: "" });
    const [selectedCompany, setSelectedCompany] = useState<{ id: number, name: string }>({ id: 0, name: "" });
    const [selectedCompanyAdmin, setSelectedCompanyAdmin] = useState<{ id: number, name: string }>({ id: 0, name: "" });
    const [vesselTypes, setVesselTypes] = useState<string[]>([]);
    const [selectedVesselType, setSelectedVesselType] = useState<string>("");
    const [isAssignReportModalOpen, setIsAssignReportModalOpen] = useState(false);
    const [isAddReportModalOpen, setIsAddReportModalOpen] = useState(false);
    const [isAddTabModalOpen, setIsAddTabModalOpen] = useState(false);
    const [isEditReportModalOpen, setIsEditReportModalOpen] = useState(false);
    const [isEditTabModalOpen, setIsEditTabModalOpen] = useState(false);
    const [isAddFieldModalOpen, setIsAddFieldModalOpen] = useState(false);
    const [isEditFieldModalOpen, setIsEditFieldModalOpen] = useState(false);
    const [isDeleteFieldModalOpen, setIsDeleteFieldModalOpen] = useState(false);
    const [reports, setReports] = useState<Reports[]>([]);
    const [loadingReports, setLoadingReports] = useState<boolean>(false);
    const [fields, setFields] = useState<Fields[]>([]);
    const [selectedField, setSelectedField] = useState<Fields>();
    const [menuId, setMenuId] = useState(0);
    const [submenuId, setSubmenuId] = useState(0);
    const [reorderMode, setReorderMode] = useState(false);
    const [draggingFieldId, setDraggingFieldId] = useState<number | null>(null);
    const [dragOverFieldId, setDragOverFieldId] = useState<number | null>(null);
    const [isReorderSaving, setIsReorderSaving] = useState(false);
    const [rowsPerPage, setRowsPerPage] = useState(10); // Default to 10
    const [isFieldAdded, setIsFieldAdded] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [isConsumptionAndROB, setIsConsumptionAndROB] = useState<boolean>(false);
    const [shouldUseCompanyGroupAdmin, setShouldUseCompanyGroupAdmin] = useState(false);
    const sortFieldsByOrder = (list: Fields[]) => {
        return [...list].sort((a, b) => {
            const orderA = a.order ?? Number.MAX_SAFE_INTEGER;
            const orderB = b.order ?? Number.MAX_SAFE_INTEGER;
            if (orderA === orderB) return (a.id ?? 0) - (b.id ?? 0);
            return orderA - orderB;
        });
    };

    // Pagination logic
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const pagedFields = reorderMode ? fields : fields.slice(indexOfFirstRow, indexOfLastRow);
    const displayFields = pagedFields.filter(field => field.isActive);

    const totalPages = Math.ceil(fields.length / rowsPerPage);

    const activeReport = useMemo(
        () => reports.find(report => report.submenus?.some(tab => tab.id === submenuId)),
        [reports, submenuId]
    );
    const activeFieldCount = useMemo(() => fields.filter(field => field.isActive).length, [fields]);
    const canEdit = libraryApi.features.allowEditInCompanyMode || mode === "master";
    const showModeToggle = shouldShowOperationsLibraryModeToggle(
        libraryApi.features.showModeToggle,
        canSelectLibraryMode
    );
    const showScopeToolbar =
        showModeToggle ||
        (mode === 'company' && (libraryApi.features.showCompanyFilters || libraryApi.features.showAssignReports));

    // Fine-grained permission checks
    const hasCreateReportPermission = can('CREATE_REPORT_MENU') || isSuperAdmin || actsAsSuperadmin;
    const hasCreateTabPermission = can('CREATE_REPORT_SUBMENU') || isSuperAdmin || actsAsSuperadmin;
    const hasCreateFieldPermission = can('CREATE_REPORT_FIELD') || isSuperAdmin || actsAsSuperadmin;
    const hasUpdateFieldPermission = can('UPDATE_REPORT_FIELD') || isSuperAdmin || actsAsSuperadmin;
    const hasDeleteFieldPermission = can('DELETE_REPORT_FIELD') || isSuperAdmin || actsAsSuperadmin;

    const emptyColSpan = useMemo(
        () => ((hasUpdateFieldPermission || hasDeleteFieldPermission) ? 8 : 7) + (reorderMode ? 1 : 0),
        [hasUpdateFieldPermission, hasDeleteFieldPermission, reorderMode]
    );

    const goToPage = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    useEffect(() => {
        fetchReportsList();
    }, []);

    useEffect(() => {
        if (canSelectLibraryMode) {
            fetchCompanyAdmins();
        }
        // if (roleId !== "1") {
        //     setMode("company");
        // }
        // fetchCompanies();
    }, [canSelectLibraryMode]);

    useEffect(() => {
        console.log(reports);
    }, [reports]);

    useEffect(() => {
        console.log(companies);
    }, [companies]);


    useEffect(() => {
        console.log("company admin id", selectedCompanyAdmin);
    }, [selectedCompanyAdmin]);

    useEffect(() => {
        console.log(selectedCompany);
    }, [selectedCompany]);

    useEffect(() => {
        const nextMode = coerceOperationsLibraryMode(mode, canSelectLibraryMode);
        if (nextMode !== mode) {
            setMode(nextMode);
        }
    }, [canSelectLibraryMode, mode]);

    useEffect(() => {
        if (actsAsCga && effectiveCgaId) {
            setSelectedCompanyAdmin({ id: effectiveCgaId, name: '' });
            fetchCompaniesbyCompanyAdmin(effectiveCgaId);
        }

        if (scopeUx.isCompanyAdmin) {
            const subcompanyId = Number(roleEntityId);
            setSelectedCompany({ id: subcompanyId, name: '' });
            if (libraryApi.features.showVesselFilters) {
                fetchVesselTypeByCompany(subcompanyId);
            }
        }

        if (scopeUx.isCrew && currentUser?.vessel) {
            setSelectedCompany({
                id: currentUser.vessel.companyAdmin?.id ?? 0,
                name: currentUser.vessel.companyAdmin?.name ?? '',
            });
            setSelectedVessel({
                id: currentUser.vessel.id,
                name: currentUser.vessel.fleet_name ?? '',
                imoNumber: currentUser.vessel.imoNumber ?? '',
            });
            setSelectedVesselType(currentUser.vessel.vesselType ?? '');
        }
    }, [actsAsCga, effectiveCgaId, scopeUx.isCompanyAdmin, scopeUx.isCrew, roleEntityId, libraryApi.features.showVesselFilters, currentUser]);


    const menuIdRef = useRef(menuId);
    const submenuIdRef = useRef(submenuId);
    useEffect(() => { menuIdRef.current = menuId; }, [menuId]);
    useEffect(() => { submenuIdRef.current = submenuId; }, [submenuId]);

    useEffect(() => {
        if (reports.length > 0) {
            if (isFieldAdded && submenuId) {
                handleTabClick(submenuId); // Keep current tab if field was added
                setIsFieldAdded(false); // Reset the flag
                return;
            }

            const currentMenuId = menuIdRef.current;
            const currentSubmenuId = submenuIdRef.current;

            const currentMenu = reports.find(r => r.id === currentMenuId);
            if (currentMenu) {
                const currentSubmenu = currentMenu.submenus?.find(s => s.id === currentSubmenuId);
                if (currentSubmenu) {
                    handleTabClick(currentSubmenuId);
                    return;
                } else if (currentMenu.submenus?.length > 0) {
                    const firstTabId = currentMenu.submenus[0].id;
                    handleTabClick(firstTabId);
                    setSubmenuId(firstTabId);
                    return;
                }
            }

            if (reports[0]?.submenus?.length > 0) {
                const firstTabId = reports[0].submenus[0].id;
                handleTabClick(firstTabId); // Default to first tab
                setMenuId(reports[0].id);
                setSubmenuId(firstTabId);
            }
        }
    }, [reports]);

    // When in company mode and superadmin, auto-select first company if none selected
    useEffect(() => {
        if (mode === 'company' && canSelectLibraryMode && selectedCompanyAdmin.id === 0 && companyAdmins.length > 0) {
            const firstCompany = companyAdmins[0];
            setSelectedCompanyAdmin(firstCompany);
            fetchCompaniesbyCompanyAdmin(firstCompany.id);
        }
    }, [mode, canSelectLibraryMode, selectedCompanyAdmin.id, companyAdmins]);

    useEffect(() => {
        fetchReportsList();
    }, [mode, selectedCompanyAdmin, selectedCompany, selectedVesselType, selectedVessel]);

    const handleFieldAdded = async () => {
        setIsFieldAdded(true);
        const currentTabId = submenuId;
        await fetchReportsList();
        setSubmenuId(currentTabId); // Reapply selected tab
    };

    const openAddReportModal = () => {
        setIsAddReportModalOpen(true);
    };

    const onReportAdded = async (tempId: number) => {
        fetchReportsList();
        if (!libraryApi.features.useAssignments || !libraryApi.assignReport) {
            return;
        }
        try {
            if (actsAsSuperadmin) {
                for (const companyAdmin of companyAdmins) {
                    try {
                        const response = await libraryApi.assignReport?.(tempId, {
                            mode: 'company',
                            companyGroupAdminId: companyAdmin.id,
                        });
                        console.log(`Template assigned to company Admin ID ${companyAdmin.id}:`, response);
                    } catch (err) {
                        console.error(`Failed to assign template to company Admin ID ${companyAdmin.id}:`, err);
                    }
                }
            } else if (actsAsCga) {
                try {
                    const response = await libraryApi.assignReport?.(tempId, mutationContext);
                    console.log(`Template assigned to company Admin ID ${selectedCompanyAdmin.id}:`, response);
                } catch (err) {
                    console.error(`Failed to assign template to company Admin ID ${selectedCompanyAdmin.id}:`, err);
                }
            }
        } catch (err) {
            console.error('Failed to assign template:', err);
        }
    };

    const fetchCompanyAdmins = async () => {
        try {
            const companyAdminList = await getCompanyAdminList();
            console.log('Company Admin List:', companyAdminList);

            const filteredCompanies = companyAdminList.filter(company => company.active === true);

            setCompanyAdmins(filteredCompanies);
        } catch (error) {
            console.error('Failed to fetch company admin list:', error);
        }
    };

    const fetchCompaniesbyCompanyAdmin = async (companyAdminId: number) => {
        try {
            const companyList = await getCompanyList();
            console.log('Company List:', companyList);

            const filteredCompanies = companyList.filter(company => company.cga?.id === companyAdminId && company.active === true);
            console.log("Filtered companies:", filteredCompanies);

            setCompanies(filteredCompanies);
            return filteredCompanies;
        } catch (error) {
            console.error('Failed to fetch company list:', error);
            return [];
        }
    };

    const fetchVesselTypeByCompanyAdmin = async (companyAdminId: number) => {

        try {
            const vesselList = await getVesselTypeList();

            console.log(vesselList);
            const filteredVessels = vesselList.filter(
                (vessel) => vessel.companyGroupAdmin?.id === companyAdminId
            );
            console.log(filteredVessels);

            // Extract vesselType names (including duplicates, if needed)
            const vesselTypeNames = filteredVessels.map(v => v.vesselType);

            // Optional: remove duplicates if you only want distinct names
            const distinctVesselTypes = Array.from(new Set(vesselTypeNames));
            setVesselTypes(distinctVesselTypes);

        } catch (error) {
            console.error('Failed to fetch vesseltype by company list:', error);
        }
    };

    const fetchVesselTypeByCompany = async (companyId: number) => {

        try {
            const vesselList = await getVesselTypeList();

            console.log(vesselList);
            const filteredVessels = vesselList.filter(
                (vessel) => vessel.companyAdmin?.id === companyId
            );
            console.log(filteredVessels);

            // Extract vesselType names (including duplicates, if needed)
            const vesselTypeNames = filteredVessels.map(v => v.vesselType);

            // Optional: remove duplicates if you only want distinct names
            const distinctVesselTypes = Array.from(new Set(vesselTypeNames));
            setVesselTypes(distinctVesselTypes);


        } catch (error) {
            console.error('Failed to fetch vesseltype by company list:', error);
        }
    };

    const fetchVesselsByVesselType = async (vesselType: string, companyId: number) => {

        try {
            const vesselList = await getVesselTypeList();
            console.log(vesselList);
            // Filter vessels by vesselType
            const filteredVessels = vesselList.filter(
                (vessel) => {
                    const matchesVesselType = vesselType ? vessel.vesselType === vesselType : true;
                    if (shouldUseCompanyGroupAdmin) {
                        console.log(selectedCompanyAdmin.id);
                        return (
                            matchesVesselType &&
                            vessel.companyGroupAdmin?.id === selectedCompanyAdmin.id
                        );
                    } else {
                        return (
                            matchesVesselType &&
                            (companyId === 0 || vessel.companyAdmin?.id === companyId)
                        );
                    }
                }
            );

            console.log(filteredVessels);

            // Extract only required fields: imoNumber, fleet_name, and id
            const vesselDetails = filteredVessels.map((vessel) => ({
                id: vessel.id,
                name: vessel.fleet_name,
                imoNumber: vessel.imoNumber,
            }));

            setVessels(vesselDetails);

        } catch (error) {
            console.error('Failed to fetch vessel by vesseltype list:', error);
        }
    };

    const handleTabClick = async (tabId: number) => {
        try {

            // Flatten all submenus from all reports
            const allSubmenus = reports.flatMap(report => report.submenus);

            // Find the submenu (tab) that matches the given tabId
            const targetTab = allSubmenus.find(submenu => submenu.id === tabId);

            if (targetTab) {
                // Set its fields to state
                setFields(sortFieldsByOrder(targetTab.fields || []));
                setCurrentPage(1);            // ✅ Reset pagination to first page
            } else {
                console.warn(`No tab found with ID ${tabId}`);
                setFields([]);
                setCurrentPage(1);            // ✅ Also reset page here to avoid stale pagination
            }

            // const allfields = await getFieldsForTab(auth.jwt, tabId);

            // setFields(allfields);

        } catch (err) {
            console.error('Failed to fetch fields list:', err);
            return [];
        }
    };

    const fetchReportsList = async () => {
        try {
            setLoadingReports(true);
            if (requiresExplicitCompanyTarget || requiresAuthenticatedCompanyScope) {
                setReports([]);
                setFields([]);
                return [];
            }

            let templateIDs: number[] = [];
            let filteredList: any[] = [];
            let allReports: any[] = [];

            const useAssignments = libraryApi.features.useAssignments && mode === "company";
            if (!useAssignments || selectedCompanyAdmin?.id === 0) {
                const fetched = await fetchTemplateIds();
                console.log("Basetemplates:", fetched);
                templateIDs = fetched ?? []; // fallback to [] if undefined
            } else {
                const templateList = await fetchAssignedTemplateIds();
                console.log(templateList);
                filteredList = templateList; // Save for later customTemplate.menus use

                console.log(filteredList);
                templateIDs = templateList
                    .filter(item => item.customTemplate == null && item.template != null)
                    .map(item => item.template!.id);

                console.log("Filtered Template IDs:", templateIDs);
            }

            console.log(filteredList);

            // ✅ Add customTemplate.menus as additional "reports"
            const customMenus = filteredList
                .filter(item => item.customTemplate && Array.isArray(item.customTemplate.menus))
                .flatMap(item => item.customTemplate!.menus!);


            console.log(customMenus);

            if (templateIDs && templateIDs.length > 0) {
                console.log("Final templateIDs:", templateIDs);

                if (templateIDs && templateIDs.length > 0) {
                    const reportsPerTemplateResults = await Promise.allSettled(
                        templateIDs.map(templateId => libraryApi.getReportsForTemplate(templateId))
                    );

                    console.log(reportsPerTemplateResults);

                    const reportsPerTemplate = reportsPerTemplateResults
                        .filter(result => result.status === 'fulfilled')
                        .map(result => (result as PromiseFulfilledResult<Reports[]>).value);

                    console.log(reportsPerTemplate);

                    allReports = reportsPerTemplate.flat();

                    console.log(allReports);

                    // Fetch tabs for each report
                    // const reportsWithTabs = await Promise.all(
                    //     allReports.map(async (report) => {
                    //         const submenus = await getTabsForReport(auth.jwt, report.id);
                    //         return { ...report, submenus }; // attach tabs to report
                    //     })
                    // );
                    // console.log("reports list: ", reportsWithTabs)
                } else {
                    console.warn('No template IDs found.');
                }
            }
            allReports = [...allReports, ...customMenus];

            console.log("All reports including customTemplate menus:", allReports);
            setReports(allReports); // ✅ Set state directly
        } catch (err) {
            console.error('Failed to fetch reports list:', err);
            return [];
        } finally {
            setLoadingReports(false);
        }
    };

    const persistFieldOrder = async (orderedFields: Fields[]) => {
        if (!orderedFields.length || !submenuId) return;
        const customTemplateId = activeReport?.customTemplateId;
        try {
            setIsReorderSaving(true);
            await Promise.all(
                orderedFields.map((field, index) => {
                    const order = index + 1;
                    const options = field.optionsJson && field.optionsJson.length ? field.optionsJson : undefined;
                    return mutationApi.updateField(
                        field.submenuId ?? submenuId,
                        field.id,
                        field.label,
                        field.fieldType,
                        field.required,
                        field.readOnly,
                        field.warningOnly ?? undefined,
                        field.isActive,
                        field.unit,
                        field.minValue,
                        field.maxValue,
                        options,
                        order
                    );
                })
            );
            setIsFieldAdded(true);
            await fetchReportsList();
            handleTabClick(submenuId);
        } catch (err) {
            console.error('Failed to update field order:', err);
        } finally {
            setIsReorderSaving(false);
        }
    };

    const applyFieldReorder = async (sourceId: number, targetId: number) => {
        if (sourceId === targetId) return;
        const activeFields = fields.filter(field => field.isActive);
        const inactiveFields = fields.filter(field => !field.isActive);
        const fromIndex = activeFields.findIndex(field => field.id === sourceId);
        const toIndex = activeFields.findIndex(field => field.id === targetId);
        if (fromIndex < 0 || toIndex < 0) return;
        const reorderedActive = [...activeFields];
        const [moved] = reorderedActive.splice(fromIndex, 1);
        reorderedActive.splice(toIndex, 0, moved);
        const orderedFields = reorderedActive.map((field, index) => ({
            ...field,
            order: index + 1,
        }));
        const combined = [...orderedFields, ...inactiveFields];
        setFields(combined);
        await persistFieldOrder(combined);
    };

    const handleDragStart = (fieldId: number) => {
        if (!reorderMode || isReorderSaving) return;
        setDraggingFieldId(fieldId);
    };

    const handleDragOver = (event: DragEvent<HTMLTableRowElement>, fieldId: number) => {
        if (!reorderMode) return;
        event.preventDefault();
        if (dragOverFieldId !== fieldId) {
            setDragOverFieldId(fieldId);
        }
    };

    const handleDrop = async (fieldId: number) => {
        if (!reorderMode || isReorderSaving || draggingFieldId === null) return;
        const sourceId = draggingFieldId;
        setDraggingFieldId(null);
        setDragOverFieldId(null);
        await applyFieldReorder(sourceId, fieldId);
    };

    const handleDragEnd = () => {
        setDraggingFieldId(null);
        setDragOverFieldId(null);
    };

    const resolveCompanyGroupId = () => {
        const crewCompanyGroupId = (currentUser as any)?.vessel?.companyGroupAdmin?.id;
        if (crewCompanyGroupId) {
            return crewCompanyGroupId;
        }
        if (selectedCompanyAdmin?.id) {
            return selectedCompanyAdmin.id;
        }
        if (effectiveCgaId) {
            return effectiveCgaId;
        }
        if (companyGroupAdminId) {
            return Number(companyGroupAdminId);
        }
        return undefined;
    };

    const resolveCompanyAdminId = () => {
        if (scopeUx.isCrew) {
            return (currentUser as any)?.vessel?.companyAdmin?.id || undefined;
        }
        if (scopeUx.isCompanyAdmin) {
            return selectedCompany?.id || Number(roleEntityId) || undefined;
        }
        return selectedCompany?.id ? selectedCompany.id : undefined;
    };

    const requiresExplicitCompanyTarget =
        mode === 'company' && canSelectLibraryMode && !resolveCompanyGroupId();

    const requiresAuthenticatedCompanyScope =
        mode === 'company' && !canSelectLibraryMode && !resolveCompanyGroupId();

    const mutationContext = useMemo<LibraryMutationContext>(() => buildLibraryMutationContext(
        currentUser,
        roleDetails,
        mode,
        {
            selectedCompanyGroupAdminId: resolveCompanyGroupId(),
            selectedCompanyAdminId: resolveCompanyAdminId(),
            selectedVesselId: selectedVessel.id || undefined,
            selectedVesselType,
        }
    ), [currentUser, roleDetails, mode, selectedCompanyAdmin?.id, effectiveCgaId, companyGroupAdminId, selectedCompany?.id, selectedVessel.id, selectedVesselType]);

    const mutationApi = useMemo<LibraryApi>(() => ({
        ...libraryApi,
        createMenuItem: (templateId, reportName, active) =>
            libraryApi.createMenuItem(templateId, reportName, active, mutationContext),
        updateMenuItem: (templateId, reportId, reportName, active, order) => {
            if (mutationContext.mode === 'company' && !hasLibraryMutationTarget(mutationContext)) {
                return Promise.reject(new Error('Please select a Company, Sub Company, Vessel Type, or Vessel.'))
            }
            return libraryApi.updateMenuItem(templateId, reportId, reportName, active, order, mutationContext)
        },
        createSubmenuItem: (menuId, name, active, order) =>
            libraryApi.createSubmenuItem(menuId, name, active, order, mutationContext),
        updateSubMenuItem: (menuId, submenuId, name, active, order) =>
            libraryApi.updateSubMenuItem(menuId, submenuId, name, active, order, mutationContext),
        deleteSubMenuItem: (menuId, submenuId) =>
            libraryApi.deleteSubMenuItem(menuId, submenuId, mutationContext),
        createField: (submenuId, label, fieldType, readOnly, required, active, warningOnly, unit, minValue, maxValue, options) =>
            libraryApi.createField(submenuId, label, fieldType, readOnly, required, active, warningOnly, unit, minValue, maxValue, options, mutationContext),
        updateField: (submenuId, fieldId, label, fieldType, required, readOnly, warningOnly, active, unit, minValue, maxValue, options, order) => {
            if (mutationContext.mode === 'company' && !hasLibraryMutationTarget(mutationContext)) {
                return Promise.reject(new Error('Please select a Company, Sub Company, Vessel Type, or Vessel.'))
            }
            return libraryApi.updateField(submenuId, fieldId, label, fieldType, required, readOnly, warningOnly, active, unit, minValue, maxValue, options, order, mutationContext)
        },
        deleteField: (submenuId, fieldId) =>
            libraryApi.deleteField(submenuId, fieldId, mutationContext),
    }), [libraryApi, mutationContext]);

    const fetchTemplateIds = async () => {
        try {

            const templateList = await libraryApi.getTemplateList({
                companyGroupAdminId: resolveCompanyGroupId(),
                companyAdminId: resolveCompanyAdminId(),
            });
            console.log(templateList);

            // Extract all IDs
            const templateIds: number[] = templateList
                .map(template => template.id);

            return templateIds;
        } catch (err) {
            console.error('Failed to fetch template list:', err);
        }
    };

    const fetchAssignedTemplateIds = async (): Promise<AssignedReports[]> => {
        try {

            let filteredList: any[] = [];

            const resolvedCompanyGroupId = resolveCompanyGroupId() ?? 0;

            if (resolvedCompanyGroupId !== 0 && selectedCompany?.id === 0 && selectedVesselType === '' && selectedVessel?.id === 0) {
                if (!libraryApi.getAssignedTemplatesByCompanyAdmin) {
                    return [];
                }
                filteredList = await libraryApi.getAssignedTemplatesByCompanyAdmin(resolvedCompanyGroupId);
                console.log(filteredList);
            } else if (resolvedCompanyGroupId !== 0 && selectedCompany.id !== 0 && selectedVesselType === '' && selectedVessel?.id === 0) {
                if (!libraryApi.getAssignedTemplatesByCompany) {
                    return [];
                }
                filteredList = await libraryApi.getAssignedTemplatesByCompany(resolvedCompanyGroupId, selectedCompany.id);
                console.log(filteredList);
            } else if (resolvedCompanyGroupId !== 0 && selectedVesselType !== '' && selectedVessel?.id === 0) {
                if (!libraryApi.getAssignedTemplatesByVesselType) {
                    return [];
                }
                filteredList = await libraryApi.getAssignedTemplatesByVesselType(resolvedCompanyGroupId, selectedCompany.id, selectedVesselType);
                console.log(filteredList);
            } else if (resolvedCompanyGroupId !== 0 && selectedVesselType !== '' && selectedVessel?.id !== 0) {
                if (!libraryApi.getAssignedTemplatesByVesselId) {
                    return [];
                }
                filteredList = await libraryApi.getAssignedTemplatesByVesselId(resolvedCompanyGroupId, selectedCompany.id, selectedVesselType, selectedVessel.id);
                console.log(filteredList);
            }

            console.log(filteredList);

            return filteredList;

        } catch (err) {
            console.error('Failed to fetch template list:', err);
            return [];
        }
    };

    return (
        <>
            <ToastContainer position='top-center' style={{ top: '5rem' }} />
            <div
                className='app-main flex-column flex-row-fluid'
                style={{
                    display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, height: 'calc(100vh - 110px)', overflow: 'hidden',
                }}
            >
                <div className='d-flex flex-column flex-column-fluid' style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }} >
                    <div
                        className='app-content flex-column-fluid d-flex flex-column'
                        style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
                    >
                        <div className='card d-flex flex-column border-top'
                            style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
                        >
                        {/* Header */}

                        <div className='align-items-center justify-content-start py-4 gap-2 gap-md-12 px-5 bg-white'  >
                            <div>
                                <div className='d-flex align-items-center gap-1'>
                                    <h3 className='card-title fw-bold text-dark'>{title}</h3>
                                </div>
                                {showScopeToolbar && (
                                    <div className="library-toolbar d-flex align-items-end justify-content-between flex-wrap gap-3 mt-2" style={{ background: '#f8f9fa', border: '1px solid #e5e7eb', borderRadius: 12, padding: '12px 14px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                                        <div
                                            className="library-toolbar__left"
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'auto 220px 220px 200px 220px',
                                                gap: '12px',
                                                alignItems: 'end',
                                                flex: 1,
                                                minWidth: 0,
                                            }}
                                        >
                                            {showModeToggle && (
                                                <div className="lib-field" style={{ minWidth: 140, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                    <span className="text-muted small">Mode</span>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <input
                                                            type="radio"
                                                            name="mode"
                                                            value="master"
                                                            checked={mode === "master"}
                                                            onChange={(e) => {
                                                                setMode(e.target.value as 'master' | 'company');
                                                                setVessels([]);
                                                                setVesselTypes([]);
                                                                setCompanies([]);
                                                                if (canSelectLibraryMode) {
                                                                    setSelectedCompanyAdmin({ id: 0, name: "" });
                                                                }
                                                                setSelectedCompany({ id: 0, name: "" });
                                                                setSelectedVesselType("");
                                                                setSelectedVessel({ id: 0, name: "", imoNumber: "" });
                                                            }}
                                                        />
                                                        <label className="mb-0">Master</label>
                                                        <input
                                                            type="radio"
                                                            name="mode"
                                                            value="company"
                                                            checked={mode === "company"}
                                                            onChange={(e) => {
                                                                setMode(e.target.value as 'master' | 'company');
                                                                if (canSelectLibraryMode) {
                                                                    setSelectedCompanyAdmin({ id: 0, name: "" });
                                                                }
                                                                setSelectedCompany({ id: 0, name: "" });
                                                                setSelectedVesselType("");
                                                                setSelectedVessel({ id: 0, name: "", imoNumber: "" });
                                                            }}
                                                        />
                                                        <label className="mb-0">Company</label>
                                                    </div>
                                                </div>
                                            )}

                                            {mode === "company" && libraryApi.features.showCompanyFilters && (
                                                <>
                                                    {canSelectLibraryMode && (
                                                        <div className="lib-field" style={{ minWidth: 200, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                            <span className="text-muted small">Company</span>
                                                            <SearchableSelect
                                                                value={selectedCompanyAdmin.id}
                                                                onChange={(e) => {
                                                                    setSelectedCompany({ id: 0, name: "" })
                                                                    setSelectedVessel({ id: 0, name: "", imoNumber: "" });
                                                                    setSelectedVesselType("")
                                                                    const selectedId = parseInt(e.target.value, 10);
                                                                    const selected = companyAdmins.find(c => c.id === selectedId);
                                                                    setSelectedCompanyAdmin(selected || { id: 0, name: "" });
                                                                    fetchCompaniesbyCompanyAdmin(selectedId).then((companyList) => {
                                                                        if (libraryApi.features.showVesselFilters) {
                                                                            if (companyList?.length === 0) {
                                                                                fetchVesselTypeByCompanyAdmin(selectedId); // fallback
                                                                                setShouldUseCompanyGroupAdmin(true);
                                                                                fetchVesselsByVesselType("", 0);
                                                                            } else {
                                                                                fetchVesselTypeByCompany(0);
                                                                                setShouldUseCompanyGroupAdmin(false);
                                                                                setVessels([]);
                                                                            }
                                                                        }
                                                                    });

                                                                }}
                                                                className='form-select form-select-sm'
                                                                style={{ height: 40 }}
                                                            >
                                                                <option value="">--Select Company--</option>
                                                                {companyAdmins.map((companyAdmin, index) => (
                                                                    <option key={index} value={companyAdmin.id}>{companyAdmin.name}</option>
                                                                ))}
                                                            </SearchableSelect>
                                                        </div>
                                                    )}
                                                    {(canSelectLibraryMode || actsAsCga) && (
                                                        <div className="lib-field" style={{ minWidth: 200, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                            <span className="text-muted small">Sub Company</span>
                                                            <SearchableSelect
                                                                value={selectedCompany.id}
                                                                onChange={(e) => {
                                                                    setSelectedVessel({ id: 0, name: "", imoNumber: "" });
                                                                    setSelectedVesselType("")
                                                                    const selectedId = parseInt(e.target.value, 10);
                                                                    const selected = companies.find(c => c.id === selectedId);
                                                                    setSelectedCompany(selected || { id: 0, name: "" });
                                                                    if (libraryApi.features.showVesselFilters) {
                                                                        fetchVesselTypeByCompany(selectedId)
                                                                        fetchVesselsByVesselType("", selectedId);
                                                                    }

                                                                }}
                                                                className='form-select form-select-sm'
                                                                style={{ height: 40 }}
                                                            >
                                                                <option value="">--Select Sub Company--</option>
                                                                {companies.map((company, index) => (
                                                                    <option key={index} value={company.id}>{company.name}</option>
                                                                ))}
                                                            </SearchableSelect>
                                                        </div>
                                                    )}
                                                    {libraryApi.features.showVesselFilters && (
                                                        <>
                                                            <div className="lib-field" style={{ minWidth: 200, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                                <span className="text-muted small">Vessel Type</span>
                                                                <SearchableSelect
                                                                    onChange={(e) => {
                                                                        setSelectedVessel({ id: 0, name: "", imoNumber: "" });
                                                                        setSelectedVesselType(e.target.value)
                                                                        fetchVesselsByVesselType(e.target.value, selectedCompany?.id)
                                                                    }}
                                                                    className='form-select form-select-sm'
                                                                    style={{ height: 40 }}
                                                                >
                                                                    <option value="">Select Vessel Type</option>
                                                                    {vesselTypes.map((type, index) => (
                                                                        <option key={index} value={type}>{type}</option>
                                                                    ))}
                                                                </SearchableSelect>
                                                            </div>
                                                            <div className="lib-field" style={{ minWidth: 200, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                                <span className="text-muted small">Vessel</span>
                                                                <SearchableSelect
                                                                    onChange={(e) => {
                                                                        const selectedId = parseInt(e.target.value, 10);
                                                                        const selected = vessels.find(v => v.id === selectedId);
                                                                        setSelectedVessel(selected || { id: 0, name: "", imoNumber: "" });
                                                                    }}
                                                                    className='form-select form-select-sm'
                                                                    style={{ height: 40 }}
                                                                >
                                                                    <option value="">Select Vessel</option>
                                                                    {vessels.map((vessel) => (
                                                                        <option key={vessel.id} value={vessel.id}>
                                                                            {vessel.name} (IMO: {vessel.imoNumber})
                                                                        </option>
                                                                    ))}
                                                                </SearchableSelect>
                                                            </div>
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </div>

                                        {libraryApi.features.showAssignReports && (
                                            <div className="lib-toolbar__right d-flex align-items-end" style={{ flexShrink: 0 }}>
                                                <button
                                                    className="btn btn_primary ms-auto"
                                                    onClick={() => { setIsAssignReportModalOpen(true) }}
                                                    style={{ height: 40, display: 'inline-flex', alignItems: 'center', paddingInline: '14px' }}
                                                >
                                                    Assign Reports
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className='d-flex flex-grow-1' style={{ minHeight: 0, overflow: 'hidden' }}>
                            <div className='px-3 pe-3 border-end py-3 bg-white' style={{ flex: 1, minWidth: '280px', maxWidth: '380px', minHeight: 0, overflowY: 'auto', paddingBottom: '3rem' }}>
                                <div className='d-flex justify-content-between align-items-center mb-1'>
                                    <h5 className='mb-0'>Reports</h5>
                                    {
                                        canEdit && (
                                            <div className='d-flex gap-2'>
                                                {reports.length !== 0 &&

                                                    <button
                                                        className="action-btn d-flex align-items-center justify-content-center"
                                                        onClick={() => { setIsEditReportModalOpen(true) }}
                                                    >
                                                        <KTSVG path='/media/map/settings.svg' className='svg-icon-1' />
                                                    </button>
                                                }
                                                {hasCreateReportPermission && mode === 'master' && (
                                                    <button
                                                        className="action-btn d-flex align-items-center justify-content-center"
                                                        style={{ color: '#1C325B' }}
                                                        onClick={() => openAddReportModal()}
                                                    >
                                                        <KTSVG path='/media/map/zoom-in.svg' className='svg-icon-1' />
                                                    </button>
                                                )}
                                            </div>

                                        )
                                    }
                                </div>
                                {loadingReports ? (
                                    <SidebarTreeSkeleton count={4} />
                                ) : requiresExplicitCompanyTarget ? (
                                    <div className='text-muted small py-4 text-center'>
                                        Select a company from the filters above to view assigned reports.
                                    </div>
                                ) : reports.filter(report => report.isActive).length === 0 ? (
                                    <div className='text-muted small py-4 text-center'>
                                        No reports assigned for this company.
                                    </div>
                                ) : (
                                    reports.filter(report => report.isActive).map((report, index) => (
                                    <div key={report.id} className="accordion" id="reportAccordion">
                                        {/* <!-- Report Type 1 --> */}
                                        <div key={report.id} className="accordion-item">
                                            <h2 className="accordion-header position-relative" id={`heading-${report.id}`}>
                                                <button
                                                    className={`accordion-button ${menuId === report.id ? '' : 'collapsed'}`}
                                                    type="button"
                                                    data-bs-toggle="collapse"
                                                    data-bs-target={`#report-${report.id}`}
                                                    aria-expanded={menuId === report.id ? 'true' : 'false'}
                                                    aria-controls={`report-${report.id}`}
                                                    onClick={() => { setMenuId(report.id); }}>
                                                    {report.name}
                                                </button>
                                            </h2>
                                            <div id={`report-${report.id}`}
                                                className={`accordion-collapse collapse ${menuId === report.id ? 'show' : ''}`}
                                                aria-labelledby={`heading-${report.id}`} data-bs-parent="#reportAccordion">
                                                <div className="accordion-body" style={{ maxWidth: '100%' }}>
                                                    <div className='d-flex justify-content-between align-items-center'>
                                                        <h5 className='mb-0'>Tabs</h5>
                                                        {
                                                            canEdit && (
                                                                <div className='d-flex gap-2'>
                                                                    {report.submenus.length !== 0 &&
                                                                        <button
                                                                            className="action-btn d-flex align-items-center justify-content-center"
                                                                            onClick={() => {
                                                                                setMenuId(report.id);
                                                                                setIsEditTabModalOpen(true)
                                                                            }}
                                                                        >
                                                                            <KTSVG path='/media/map/settings.svg' className='svg-icon-1' />
                                                                        </button>
                                                                    }
                                                                    {hasCreateTabPermission && (
                                                                        <button
                                                                            className="action-btn d-flex align-items-center justify-content-center"
                                                                            onClick={() => {
                                                                                setMenuId(report.id);
                                                                                setIsAddTabModalOpen(true)
                                                                            }}
                                                                        >
                                                                            <KTSVG path='/media/map/zoom-in.svg' className='svg-icon-1 ' />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )
                                                        }
                                                    </div>
                                                    <div className="mt-4">
                                                        {report.submenus.filter(tab => tab.isActive).length === 0 ? (
                                                            <div className='text-center mt-2 text-muted'>No tabs available</div>
                                                        ) : (
                                                            <div className="mt-3 p-4 border rounded-1" style={{ borderColor: '#D1D5DB' }}>
                                                                {report.submenus.length > 0 && (report.submenus.filter(tab => tab.isActive).map((tab) => {
                                                                    const isActive = tab.id === submenuId; // ← define this inside the map
                                                                    return (
                                                                        <div
                                                                            key={tab.id}
                                                                            onClick={() => {
                                                                                setReorderMode(false);
                                                                                setDraggingFieldId(null);
                                                                                setDragOverFieldId(null);
                                                                                if (libraryApi.features.allowConsumptionTab && tab.name === "Consumption & ROB") {
                                                                                    setIsConsumptionAndROB(true);
                                                                                } else {
                                                                                    setIsConsumptionAndROB(false);
                                                                                }
                                                                                handleTabClick(tab.id);
                                                                                setSubmenuId(tab.id);
                                                                            }}
                                                                            className={`mt-3 p-4 border rounded-1 ${isActive ? 'active-tab' : ''} hover-shadow`}
                                                                            style={{
                                                                                backgroundColor: isActive ? '#e2e8f0' : '#F9FAFB',
                                                                                borderColor: isActive ? '#1C325B' : '#D1D5DB',
                                                                                cursor: 'pointer',
                                                                                fontWeight: isActive ? '500' : 'normal'
                                                                            }}
                                                                        >
                                                                            {tab.name}
                                                                        </div>
                                                                    );
                                                                }))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    ))
                                )}

                            </div>
                            <div className='px-5 py-3 d-flex flex-column bg-white' style={{ flex: 3, minWidth: 0, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', paddingBottom: '3rem' }}>

                                {(libraryApi.features.allowConsumptionTab && isConsumptionAndROB) ? <div className="container mt-4 pb-5">
                                    <h3>Consumption and ROB</h3>
                                    <p className="text-muted small">
                                        Select a vessel in the filters, then a voyage. Data loads and saves via the same consumption API as position reports; permissions and scope are enforced on the server.
                                    </p>
                                    <ConsumptionAndROB vesselId={selectedVessel.id > 0 ? selectedVessel.id : undefined} /> </div> :
                                    <>
                                        <div className='d-flex justify-content-between align-items-center mb-1 flex-shrink-0'>
                                            <h5 className="mb-0">Fields</h5>
                                            <div className="d-flex align-items-center gap-2">
                                                {hasCreateFieldPermission && (
                                                    <button
                                                        className="action-btn d-flex align-items-center justify-content-center"
                                                        onClick={() => { setIsAddFieldModalOpen(true) }}
                                                    >
                                                        <KTSVG path='/media/map/zoom-in.svg' className='svg-icon-1' />
                                                    </button>
                                                )}
                                                {hasUpdateFieldPermission && (
                                                    <button
                                                        className="btn btn-sm btn-light"
                                                        type="button"
                                                        disabled={activeFieldCount < 2 || isReorderSaving}
                                                        onClick={() => {
                                                            setReorderMode((prev) => !prev);
                                                            setCurrentPage(1);
                                                        }}
                                                    >
                                                        {isReorderSaving ? 'Saving...' : reorderMode ? 'Done' : 'Reorder'}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        {reorderMode && (
                                            <div className="text-muted small mb-2 flex-shrink-0">
                                                Drag fields to rearrange. Changes save automatically.
                                            </div>
                                        )}
                                        <div className="report-table table-responsive" style={{ overflowX: 'auto', overflowY: 'visible', marginBottom: '1.5rem' }}>
                                            <table className="table table-bordered align-middle mb-0">
                                                <thead className="table-header">
                                                    <tr>
                                                        {reorderMode && <th rowSpan={2} style={{ width: '48px' }}>Move</th>}
                                                        <th rowSpan={2}>Label</th>
                                                        <th rowSpan={2}>Type</th>
                                                        <th rowSpan={2}>Required</th>
                                                        <th rowSpan={2}>Unit</th>
                                                        <th rowSpan={2}>Validation Type</th>
                                                        <th colSpan={2}>Validation Range</th>
                                                        {(hasUpdateFieldPermission || hasDeleteFieldPermission) && (<th rowSpan={2}>Actions</th>)}
                                                    </tr>
                                                    <tr>
                                                        <th>Min Value</th>
                                                        <th>Max Value</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="table-body">
                                                    {loadingReports ? (
                                                        <TableSkeleton rows={6} cols={reorderMode ? 9 : 8} />
                                                    ) : requiresExplicitCompanyTarget ? (
                                                        <tr>
                                                            <th colSpan={emptyColSpan}>
                                                                <div className='text-center mt-4 text-muted'>Select a company from the filters above to view fields</div>
                                                            </th>
                                                        </tr>
                                                    ) : displayFields.length === 0 ? (
                                                        <tr>
                                                            <th colSpan={emptyColSpan}>
                                                                <div className='text-center mt-4 text-muted'>No fields available</div>
                                                            </th>
                                                        </tr>
                                                    ) : (
                                                        displayFields.map((field) => {
                                                            const isDragging = draggingFieldId === field.id;
                                                            const isDragOver = dragOverFieldId === field.id;
                                                            return (
                                                                <tr
                                                                    key={field.id}
                                                                    draggable={reorderMode}
                                                                    onDragStart={() => handleDragStart(field.id)}
                                                                    onDragOver={(event) => handleDragOver(event, field.id)}
                                                                    onDrop={() => handleDrop(field.id)}
                                                                    onDragEnd={handleDragEnd}
                                                                    style={{
                                                                        opacity: isDragging ? 0.6 : 1,
                                                                        backgroundColor: isDragOver ? '#F4F7FF' : undefined,
                                                                        cursor: reorderMode ? 'move' : undefined,
                                                                    }}
                                                                >
                                                                    {reorderMode && (
                                                                        <td className="text-center">
                                                                            <span className="text-muted">::</span>
                                                                        </td>
                                                                    )}
                                                                    <td>{field.label}</td>
                                                                    <td>{field.fieldType}</td>
                                                                    <td>{field.required ? "Yes" : "No"}</td>
                                                                    <td>{field.unit}</td>
                                                                    <td>
                                                                        {field.warningOnly === "SOFT"
                                                                            ? "Soft"
                                                                            : field.warningOnly === "STRICT"
                                                                                ? "Strict"
                                                                                : null}
                                                                    </td>
                                                                    <td>{field.minValue}</td>
                                                                    <td>{field.maxValue}</td>
                                                                    {/* <td>
                                                            <span
                                                                className={`badge ${row.status === "Draft" ? "draft" : "submitted"
                                                                    }`}
                                                            >
                                                                {row.status}
                                                            </span>
                                                        </td> */}

                                                                    {
                                                                        (hasUpdateFieldPermission || hasDeleteFieldPermission) && (
                                                                            <td>
                                                                                {hasUpdateFieldPermission && (
                                                                                    <button
                                                                                        onClick={() => {
                                                                                            setSelectedField(field);
                                                                                            setIsEditFieldModalOpen(true);
                                                                                        }}
                                                                                        className="btn btn-sm px-0"
                                                                                    >
                                                                                        <KTSVG path='/media/map/edit-active.svg' className='' />
                                                                                    </button>
                                                                                )}
                                                                                {hasDeleteFieldPermission && (
                                                                                    <button
                                                                                        onClick={() => {
                                                                                            setSelectedField(field);
                                                                                            setIsDeleteFieldModalOpen(true);
                                                                                        }}
                                                                                        className="btn btn-sm px-0">
                                                                                        <KTSVG path='/media/map/trash.svg' className='' />
                                                                                    </button>
                                                                                )}
                                                                            </td>
                                                                        )
                                                                    }

                                                                </tr>
                                                            )
                                                        }))}
                                                </tbody>
                                            </table>
                                        </div>
                                        {/* Pagination */}
                                        {!reorderMode && (
                                            <div className="pagination-wrapper d-flex justify-content-between align-items-center mt-auto pb-4 flex-shrink-0">
                                                <div>
                                                    Rows per page
                                                    <SearchableSelect
                                                        className="form-select d-inline-block w-auto ms-2"
                                                        style={{ borderRadius: "20px" }}
                                                        value={rowsPerPage}
                                                        onChange={(e) => {
                                                            setRowsPerPage(Number(e.target.value));
                                                            setCurrentPage(1); // reset to first page when changing rows per page
                                                        }}
                                                    >
                                                        <option value={10}>10</option>
                                                        <option value={20}>20</option>
                                                        <option value={50}>50</option>
                                                    </SearchableSelect>
                                                </div>

                                                <nav>
                                                    <ul className="pagination mb-0">
                                                        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                                            <button
                                                                className="page-link"
                                                                onClick={() => goToPage(currentPage - 1)}>
                                                                ‹
                                                            </button>
                                                        </li>
                                                        {Array.from({ length: totalPages }, (_, i) => (
                                                            <li key={i + 1} className={`page-item ${currentPage === i + 1 ? "active" : ""}`}>
                                                                <button className="page-link" onClick={() => goToPage(i + 1)}>{i + 1}</button>
                                                            </li>
                                                        ))}

                                                        <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                                            <button className="page-link" onClick={() => goToPage(currentPage + 1)}>›</button>
                                                        </li>
                                                    </ul>
                                                </nav>
                                            </div>
                                        )}
                                    </>
                                }
                            </div>
                        </div>
                    </div>
                    {isAddReportModalOpen && (
                        <AddReportModal
                            isOpen={isAddReportModalOpen}
                            onClose={() => { setIsAddReportModalOpen(false) }}
                            onReportAdded={onReportAdded}
                            api={mutationApi}
                            templateContext={buildLibraryTemplateFilters(mutationContext)}
                        />
                    )}
                    {isEditReportModalOpen && (
                        <EditReportModal
                            isOpen={isEditReportModalOpen}
                            onClose={() => { setIsEditReportModalOpen(false) }}
                            onReportAdded={fetchReportsList}
                            api={mutationApi}
                            templateContext={buildLibraryTemplateFilters(mutationContext)}
                        />
                    )}
                    {libraryApi.features.showAssignReports && isAssignReportModalOpen && (
                        <AssignReportModal
                            isOpen={isAssignReportModalOpen}
                            onClose={() => { setIsAssignReportModalOpen(false) }}
                            context={mutationContext}
                            onReportAssigned={fetchReportsList}
                        />
                    )}
                    {isAddTabModalOpen && (
                        <AddTabModal
                            isOpen={isAddTabModalOpen}
                            onClose={() => { setIsAddTabModalOpen(false) }}
                            menuId={menuId}
                            onTabAdded={fetchReportsList}
                            api={mutationApi}
                            allowConsumptionTab={libraryApi.features.allowConsumptionTab}
                        />
                    )}
                    {isEditTabModalOpen && (
                        <EditTabModal
                            isOpen={isEditTabModalOpen}
                            onClose={() => { setIsEditTabModalOpen(false) }}
                            menuId={menuId}
                            onTabAdded={fetchReportsList}
                            api={mutationApi}
                            templateContext={buildLibraryTemplateFilters(mutationContext)}
                        />
                    )}
                    {isAddFieldModalOpen && (
                        <AddFieldModal isOpen={isAddFieldModalOpen}
                            onClose={() => { setIsAddFieldModalOpen(false) }}
                            submenuId={submenuId}
                            onFieldAdded={handleFieldAdded}
                            api={mutationApi}
                        />
                    )}
                    {isEditFieldModalOpen && (
                        <EditFieldModal
                            isOpen={isEditFieldModalOpen}
                            onClose={() => { setIsEditFieldModalOpen(false) }}
                            fieldData={selectedField}
                            onFieldEdited={handleFieldAdded}
                            api={mutationApi}
                        />
                    )}
                    {isDeleteFieldModalOpen && (
                        <DeleteFieldModal
                            isOpen={isDeleteFieldModalOpen}
                            onClose={() => { setIsDeleteFieldModalOpen(false) }}
                            submenuId={submenuId}
                            fieldId={selectedField ? selectedField.id : 0}
                            labelName={selectedField ? selectedField.label : ""}
                            onFieldDeleted={handleFieldAdded}
                            api={libraryApi}
                        />
                    )}
                </div>
            </div>
        </div>
        </>
    )
}

export type { LibraryPageProps }
export { LibraryPageBase }
export default LibraryPageBase
